import { useEffect, useRef, useState } from "react";
import { Pause, Play, Square } from "lucide-react";
import { BoxBreath, boxPhase } from "@/components/landing/BoxBreath";
import { Ring, SOUNDS, useClock } from "@/components/landing/CalmTry";
import { chime, startWaves } from "@/components/landing/calmSound";
import { ToolFinish } from "@/components/tools/ToolFinish";
import { resetTrack, track } from "@/lib/toolTrack";

const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const phaseAt = (pattern, ms) => {
  const total = pattern.reduce((s, [, d]) => s + d, 0) * 1000;
  let t = ms % total, i = 0;
  while (t >= pattern[i][1] * 1000) { t -= pattern[i][1] * 1000; i += 1; }
  return { i, p: t / (pattern[i][1] * 1000), n: Math.floor(ms / total) * pattern.length + i, left: Math.ceil(pattern[i][1] - t / 1000) };
};

// Breathing circle: grows on inhale, rests on hold, shrinks on exhale
const BreathCircle = ({ pattern, elapsed, night }) => {
  const { i, p, left } = phaseAt(pattern, elapsed);
  const label = pattern[i][0];
  const prevScale = (k) => { let s = 0.6; for (let j = 0; j < k; j += 1) s = scaleEnd(pattern[j][0], s); return s; };
  const scaleEnd = (l, s) => (l.startsWith("Hold") ? s : /in|Sip/.test(l) ? (l.startsWith("Sip") ? 1 : 0.9) : 0.6);
  const from = prevScale(i), to = scaleEnd(label, from);
  const scale = reduced() ? 0.85 : from + (to - from) * p;
  return (
    <div className="flex flex-col items-center" data-testid="tool-breath">
      <div className="relative w-56 h-56 flex items-center justify-center">
        <div className={`absolute inset-0 rounded-full ${night ? "bg-gradient-to-br from-[#3A2E6F] to-[#7C5CBF]" : "bg-gradient-to-br from-[#A8D8EA] to-[#C9B8F0]"} shadow-lg`} style={{ transform: `scale(${scale})` }} />
        <div className={`relative text-center ${night ? "text-white" : "text-[#2E2546]"}`}>
          <div className="text-xl font-bold max-w-[11rem]" aria-live="polite" data-testid="tool-breath-cue">{label}</div>
          <div className="text-4xl font-extrabold tabular-nums mt-1" aria-hidden>{left}</div>
        </div>
      </div>
    </div>
  );
};

export const BodyScan = ({ steps, elapsed, length }) => {
  const i = Math.min(steps.length - 1, Math.floor((elapsed / length) * steps.length));
  return (
    <div className="text-center max-w-md mx-auto" data-testid="tool-scan">
      <div className="flex justify-center gap-1.5 mb-5" aria-hidden>{steps.map((label, k) => <span key={label} className={`h-1.5 w-8 rounded-full ${k <= i ? "bg-mm-primary" : "bg-mm-primary/20"}`} />)}</div>
      <p key={i} className="text-2xl font-semibold leading-snug mm-fade-up" aria-live="polite" data-testid="tool-scan-cue">{steps[i]}</p>
    </div>
  );
};

export const SoundPicker = ({ sound, setSound }) => (
  <div className="flex gap-1 rounded-full bg-white/70 p-1 w-fit" role="radiogroup" aria-label="Sound">
    {SOUNDS.map(([k, l, I]) => (
      <button key={k} role="radio" aria-checked={sound === k} onClick={() => setSound(k)} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${sound === k ? "bg-mm-primary text-white" : "text-mm-muted hover:text-mm-primary"}`} data-testid={`tool-sound-${k}`}><I size={14} />{l}</button>
    ))}
  </div>
);

export const TimedTool = ({ tool }) => {
  const length = tool.length * 1000;
  const [stage, setStage] = useState("ready");
  const [sound, setSound] = useState("off");
  const finish = () => { setStage("done"); track(tool.slug, "finish"); };
  const [elapsed, setElapsed] = useClock(stage === "run", finish, length);
  const last = useRef(-1);
  useEffect(() => (stage === "run" && sound === "waves" ? startWaves() : undefined), [stage, sound]);
  useEffect(() => {
    const n = tool.kind === "box" ? boxPhase(elapsed) : tool.kind === "breath" ? phaseAt(tool.pattern, elapsed).n : Math.floor((elapsed / length) * tool.steps.length);
    if (stage === "run" && sound === "chime" && n !== last.current) chime(n % 2 ? 440 : 528);
    last.current = n;
  }, [elapsed, stage, sound, tool, length]);
  const begin = () => { resetTrack(tool.slug); setElapsed(0); last.current = -1; setStage("run"); track(tool.slug, "start"); };

  if (stage === "done") return <ToolFinish tool={tool} onAgain={begin} />;
  return (
    <div data-testid="tool-timed">
      <div className="flex justify-center mb-6"><SoundPicker sound={sound} setSound={setSound} /></div>
      {stage === "ready" ? (
        <div className="text-center">
          <p className="text-mm-muted mb-5">{tool.helps}. Find a comfortable position, then press start.</p>
          <button onClick={begin} className="mm-btn !px-8 !py-3.5 text-lg" data-testid="tool-start"><Play size={18} />Start</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-center">
          {tool.kind === "box" && <BoxBreath elapsed={elapsed} still={reduced()} />}
          {tool.kind === "breath" && <BreathCircle pattern={tool.pattern} elapsed={elapsed} night={tool.night} />}
          {tool.kind === "scan" && <BodyScan steps={tool.steps} elapsed={elapsed} length={length} />}
          <div className="flex sm:flex-col items-center justify-center gap-3">
            <Ring elapsed={elapsed} length={length} />
            <button onClick={() => setStage(stage === "run" ? "paused" : "run")} className="mm-btn-ghost !px-4 !py-2" data-testid="tool-pause">{stage === "run" ? <><Pause size={16} />Pause</> : <><Play size={16} />Resume</>}</button>
            <button onClick={() => setStage("ready")} className="mm-btn-ghost !px-4 !py-2" data-testid="tool-stop"><Square size={14} />Stop</button>
          </div>
        </div>
      )}
    </div>
  );
};
